import { toggleIdInList } from '@griply/utils';
import { useViewOptionsSectionCollapsed } from 'features/user';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PopupMenuCheckbox } from 'shared/components/ui/popup-menu';
import {
  SideNavigationPanelItem,
  SideNavigationPanelSection,
} from 'shared/components/ui/side-navigation-panel';
import { useUpcomingCalendarViewOptions } from 'shared/contexts/upcoming-calendar-view-options';
import { TaskType } from 'shared/types/task-base';
import { ViewOptionsSectionId } from 'shared/types/view-options-section';

export type ShowSectionProps = {
  isFirst?: boolean;
};

export const ShowSection: React.FC<ShowSectionProps> = ({ isFirst }) => {
  const { t } = useTranslation();
  const { viewOptions, updateViewOptions } = useUpcomingCalendarViewOptions();
  const { hiddenTaskTypes } = viewOptions;

  const [collapsed, setCollapsed] = useViewOptionsSectionCollapsed(
    ViewOptionsSectionId.Show,
  );
  const onToggleSection = () => setCollapsed(!collapsed);

  const toggleTaskType = useCallback(
    (type: TaskType) =>
      updateViewOptions({
        hiddenTaskTypes: toggleIdInList(hiddenTaskTypes, type),
      }),
    [hiddenTaskTypes, updateViewOptions],
  );

  const onToggleTasks = () => toggleTaskType(TaskType.Task);
  const onToggleRepeating = () => toggleTaskType(TaskType.Repeating);
  const onToggleHabits = () => toggleTaskType(TaskType.Habit);
  const onToggleFocusBlocks = () =>
    updateViewOptions({ hideFocusBlocks: !viewOptions.hideFocusBlocks });
  const onToggleCalendarEvents = () =>
    updateViewOptions({
      hideExternalCalendarEvents: !viewOptions.hideExternalCalendarEvents,
    });
  const onToggleGoalDeadlines = () =>
    updateViewOptions({ hideGoalDeadlines: !viewOptions.hideGoalDeadlines });
  const onToggleCompleted = () =>
    updateViewOptions({ hideCompleted: !viewOptions.hideCompleted });

  const showTasks = !hiddenTaskTypes.includes(TaskType.Task);
  const showRepeating = !hiddenTaskTypes.includes(TaskType.Repeating);
  const showHabits = !hiddenTaskTypes.includes(TaskType.Habit);
  const showFocusBlocks = !viewOptions.hideFocusBlocks;
  const showCalendarEvents = !viewOptions.hideExternalCalendarEvents;
  const showGoalDeadlines = !viewOptions.hideGoalDeadlines;
  const showCompleted = !viewOptions.hideCompleted;

  const shown = [
    showTasks,
    showRepeating,
    showHabits,
    showFocusBlocks,
    showCalendarEvents,
    showGoalDeadlines,
    showCompleted,
  ];

  return (
    <SideNavigationPanelSection
      id={ViewOptionsSectionId.Show}
      title={t('calendar.view-options.title')}
      open={!collapsed}
      onToggle={onToggleSection}
      enabledCount={shown.filter(Boolean).length}
      totalCount={shown.length}
      isFirst={isFirst}
    >
      <SideNavigationPanelItem>
        <PopupMenuCheckbox
          label={t('calendar.view-options.tasks')}
          checked={showTasks}
          onChange={onToggleTasks}
          dimmed={!showTasks}
        />
      </SideNavigationPanelItem>

      <SideNavigationPanelItem>
        <PopupMenuCheckbox
          label={t('calendar.view-options.repeating-tasks')}
          checked={showRepeating}
          onChange={onToggleRepeating}
          dimmed={!showRepeating}
        />
      </SideNavigationPanelItem>

      <SideNavigationPanelItem>
        <PopupMenuCheckbox
          label={t('calendar.view-options.habits')}
          checked={showHabits}
          onChange={onToggleHabits}
          dimmed={!showHabits}
        />
      </SideNavigationPanelItem>

      <SideNavigationPanelItem>
        <PopupMenuCheckbox
          label={t('calendar.view-options.focus-blocks')}
          checked={showFocusBlocks}
          onChange={onToggleFocusBlocks}
          dimmed={!showFocusBlocks}
        />
      </SideNavigationPanelItem>

      <SideNavigationPanelItem>
        <PopupMenuCheckbox
          label={t('calendar.view-options.calendar-events')}
          checked={showCalendarEvents}
          onChange={onToggleCalendarEvents}
          dimmed={!showCalendarEvents}
        />
      </SideNavigationPanelItem>

      <SideNavigationPanelItem>
        <PopupMenuCheckbox
          label={t('calendar.view-options.goal-deadlines')}
          checked={showGoalDeadlines}
          onChange={onToggleGoalDeadlines}
          dimmed={!showGoalDeadlines}
        />
      </SideNavigationPanelItem>

      <SideNavigationPanelItem>
        <PopupMenuCheckbox
          label={t('calendar.view-options.completed')}
          checked={showCompleted}
          onChange={onToggleCompleted}
          dimmed={!showCompleted}
        />
      </SideNavigationPanelItem>
    </SideNavigationPanelSection>
  );
};
